function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}
/**
 *  @function themeFetch
 *  fetch() for the theme's own requests. A key names a request where only
 *  the latest call counts (eg. 'facets'): each call aborts the one still in
 *  flight under that key, along with its body read, so a slow response can
 *  never land after a newer one. options.signal, if any, aborts it as well.
 *  Resolves with the Response when it is ok. Otherwise rejects with an Error
 *  carrying the HTTP `status` and, for a JSON reply, its parsed `body` (the
 *  Ajax API's { status, message, description }, which /cart/add.js sends as
 *  text/javascript). An abort rejects with an AbortError: see isAbortError().
 */
const themeFetchControllers = new Map();
var themeFetch = function themeFetch(url, options = {}, key = null) {
  let signal = options.signal;
  if (key) {
    themeFetchControllers.get(key)?.abort();
    const controller = new AbortController();
    themeFetchControllers.set(key, controller);
    if (signal?.aborted) {
      controller.abort(signal.reason);
    } else if (signal) {
      signal.addEventListener('abort', () => controller.abort(signal.reason), { once: true });
    }
    signal = controller.signal;
  }
  return fetch(url, { ...options, signal }).then((response) => {
    if (response.ok) return response;
    const error = new Error(`Request failed: ${response.status} ${url}`);
    error.status = response.status;
    if (!/json|javascript/.test(response.headers.get('Content-Type') || '')) throw error;
    return response.json().then((body) => {
      error.body = body;
      throw error;
    }, () => {
      throw error;
    });
  });
};
// Aborts the request in flight under a themeFetch key, eg. when a cached
// result is shown instead.
themeFetch.abort = function (key) {
  themeFetchControllers.get(key)?.abort();
};
var isAbortError = function isAbortError(error) {
  return !!error && error.name === 'AbortError';
};
/**
 *  @function theme.loadNow
 *  Fetches a lazy image or iframe now, ahead of native lazy loading, which
 *  leaves alone what is hidden until it shows (eg. a product card's hover
 *  images, display: none until hovered). An iframe whose page the theme
 *  opens itself keeps its URL in data-src until then. width is the width an
 *  image will be drawn at: an image loading eagerly picks its srcset
 *  candidate from sizes, not from sizes="auto". It defaults to the image's
 *  laid-out width, or its parent's while the image is hidden; when neither
 *  is laid out, the image's fallback sizes stays.
 */
theme.loadNow = function (element, width) {
  if (!element) return;
  if (element.tagName === 'IMG') {
    width = width || element.offsetWidth || element.parentElement?.offsetWidth;
    if (width) element.sizes = `${width}px`;
  }
  if (element.dataset.src) {
    element.src = element.dataset.src;
    element.removeAttribute('data-src');
  }
  element.loading = 'eager';
};

var dispatchCustomEvent = function dispatchCustomEvent(eventName) {
  var data = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var detail = {
    detail: data
  };
  var event = new CustomEvent(eventName, data ? detail : null);
  document.dispatchEvent(event);
};
/**
 * Publish a Shopify standard event (shopify:* DOM events for analytics apps).
 * The event classes are loaded async from Shopify's CDN via a module script in
 * theme.liquid — no-op until the module is available, and never let an
 * analytics payload error break a commerce flow.
 * https://shopify.dev/docs/storefronts/themes/best-practices/standard-events
 */
var publishStandardEvent = function publishStandardEvent(eventName, payload, target) {
  const SE = window.Shopify && window.Shopify.StandardEvents;
  if (!SE || !SE[eventName]) return;
  try {
    (target || document).dispatchEvent(new SE[eventName](payload || {}));
  } catch (e) {
    console.error('Standard event failed:', eventName, e);
  }
};
/**
 *  @function standardEventCart
 *  Ajax cart JSON → CartSummary via Shopify's own converter; null when the
 *  response is not a full cart (errors, /cart/add.js line-only responses).
 */
var standardEventCart = function standardEventCart(cart) {
  const SE = window.Shopify && window.Shopify.StandardEvents;
  if (!SE || !cart || !cart.token || !cart.items) return null;
  try {
    return SE.CartViewEvent.createCartFromAjaxResponse(cart);
  } catch (e) {
    return null;
  }
};
/**
 *  @function standardEventCartError
 *  Ajax API error text → shopify:cart:error payload. The Ajax API returns no
 *  error codes, so every failure maps to the generic INVALID code.
 */
var standardEventCartError = function standardEventCartError(message) {
  return { error: String(message || 'Cart update failed'), code: 'INVALID' };
};
/**
 *  @function publishCappedCartLine
 *  After a 422 from /cart/change.js (the cart drawer and the cart page):
 *  when Shopify still moved the line (capped at what is in stock), that
 *  change goes out as the line's line-item:change:end, like any other
 *  update. `previous` is the quantity the line held before.
 */
var publishCappedCartLine = function publishCappedCartLine(key, previous, context) {
  if (Number.isNaN(previous)) return;
  themeFetch(`${theme.routes.cart_url}.js`, { headers: { 'Accept': 'application/json' } })
    .then((response) => response.json())
    .then((cart) => {
      const item = (cart.items || []).find((el) => el.key === key);
      const quantity = item ? item.quantity : 0;
      if (quantity === previous) return;
      dispatchCustomEvent('line-item:change:end', {
        key: key,
        context: context,
        quantity: quantity,
        cart: cart
      });
    })
    .catch((error) => console.error(error));
};
/**
 *  @function standardEventAmount
 *  Cents → decimal amount string ("29.90"), the MoneyV2 shape standard events expect.
 */
var standardEventAmount = function standardEventAmount(cents) {
  const value = parseInt(cents, 10);
  return (Number.isNaN(value) ? 0 : value / 100).toFixed(2);
};
/**
 * Run a DOM update inside a View Transition when the browser supports it
 * (and the user hasn't asked for reduced motion) — otherwise apply the
 * update directly. Progressive enhancement only; callers must not rely
 * on the transition happening.
 *
 * Skipped while an overlay is open: the transition layer paints above
 * ALL z-indexes, so a named element (eg. the product grid) would flash
 * in front of the click-capture dim and any open drawer. Skipped in a
 * hidden tab too, where the browser would refuse it.
 *
 * A transition that is skipped or interrupted still runs the update once
 * (or has run it), but rejects ready: that is expected, so it is handled
 * here rather than reported as unhandled. updateCallbackDone and finished
 * reject only when the update itself fails, which is logged once.
 */
var withViewTransition = function withViewTransition(update) {
  if (
    !document.startViewTransition ||
    document.visibilityState !== 'visible' ||
    window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
    OverlayManager.hasOpen()
  ) {
    update();
    return;
  }
  const transition = document.startViewTransition(update);
  const ignore = () => {};
  transition.ready.catch(ignore);
  transition.finished.catch(ignore);
  transition.updateCallbackDone.catch((error) => console.error(error));
};
/**
 *  @function reloadScripts
 *  Markup put in the page with innerHTML brings its <script> tags without
 *  running them: each is replaced by a copy that runs. A script file the
 *  page has loaded elsewhere is left alone, and one the markup has twice
 *  runs once.
 */
var reloadScripts = function reloadScripts(container) {
  const loaded = new Set(Array.from(document.scripts).filter((script) => script.src && !container.contains(script)).map((script) => script.src));
  container.querySelectorAll('script').forEach((oldScript) => {
    if (oldScript.src) {
      if (loaded.has(oldScript.src)) return;
      loaded.add(oldScript.src);
    }
    const newScript = document.createElement('script');
    Array.from(oldScript.attributes).forEach((attr) => {
      newScript.setAttribute(attr.name, attr.value);
    });
    if (!oldScript.src) {
      newScript.textContent = oldScript.textContent;
    }
    oldScript.replaceWith(newScript);
  });
};
/**
 *  @function replaceProductContent
 *  Puts fetched product markup in container in place of what it holds:
 *  quick view's content, or another product of a combined listing
 *  (product.js). A size guide or video <modal-dialog> moves itself to
 *  <body> as it connects (modal-dialog.js), so the copy the markup before
 *  left there is removed first. Its scripts run, and the dynamic checkout
 *  buttons and the 3D model viewer start.
 */
var replaceProductContent = function replaceProductContent(container, html) {
  const template = document.createElement('template');
  template.innerHTML = html;
  template.content.querySelectorAll('modal-dialog[id]').forEach((modal) => {
    Array.from(document.body.children).forEach((child) => {
      if (child.tagName === 'MODAL-DIALOG' && child.id === modal.id) child.remove();
    });
  });
  container.replaceChildren(template.content);
  reloadScripts(container);

  requestAnimationFrame(() => {
    if (typeof Shopify !== 'undefined' && Shopify.PaymentButton) {
      Shopify.PaymentButton.init();
    }
    if (window.ProductModel) {
      window.ProductModel.loadShopifyXR();
    }
  });
};
window.recentlyViewedIds = [];

// --- Shopify standard events: adapters over the theme's internal event bus ---
(function () {
  const SE = () => window.Shopify && window.Shopify.StandardEvents;

  // shopify:cart:view — cart drawer opened. The drawer HTML carries no cart
  // JSON, so fetch /cart.js (tiny, user-triggered) for an accurate payload.
  document.addEventListener('cart-drawer:open', () => {
    if (!SE()) return;
    fetch(`${theme.routes.cart_url}.js`, { headers: { 'Accept': 'application/json' } })
      .then((response) => response.json())
      .then((cart) => {
        if (!cart || !cart.token) return;
        publishStandardEvent('CartViewEvent', {
          cart: standardEventCart(cart),
          context: 'dialog'
        });
      })
      .catch(() => { /* analytics only — never surface */ });
  });

  // shopify:cart:lines-update (add) — /cart/add.js responds with the added
  // line(s) only, so the promise resolves without a full cart payload.
  // With lines[] (theme.cart.added), one event lists every line added.
  document.addEventListener('cart:item-added', (e) => {
    const detail = e.detail || {};
    if (Array.isArray(detail.lines)) {
      const lines = detail.lines
        .filter((line) => line && line.merchandiseId)
        .map((line) => ({ merchandiseId: line.merchandiseId, quantity: parseInt(line.quantity, 10) || 1 }));
      if (!lines.length) return;
      publishStandardEvent('CartLinesUpdateEvent', {
        action: 'add',
        context: detail.context || 'product',
        lines: lines,
        promise: Promise.resolve({ cart: standardEventCart(detail.cart) })
      });
      return;
    }
    const item = detail.product;
    const variantId = item && (item.variant_id || item.id);
    if (!variantId) return;
    publishStandardEvent('CartLinesUpdateEvent', {
      action: 'add',
      context: detail.context || 'product',
      lines: [{ merchandiseId: variantId, quantity: parseInt(detail.quantity, 10) || 1 }],
      promise: Promise.resolve({ cart: null })
    });
  });

  // shopify:cart:lines-update (update/remove) — /cart/change.js responds with
  // the full AJAX cart, already present in the internal event detail.
  // Skipped when no line key is carried (eg. drawer refresh() after a
  // section-only fetch): the schema needs at least one line to describe.
  // With lines[] (theme.cart.changed), one update event lists the lines
  // still in the cart and one remove event those taken out.
  document.addEventListener('line-item:change:end', (e) => {
    if (!SE()) return;
    const detail = e.detail || {};
    if (Array.isArray(detail.lines)) {
      const lines = detail.lines
        .filter((line) => line && line.key)
        .map((line) => ({ id: line.key, quantity: parseInt(line.quantity, 10) || 0 }));
      [['update', lines.filter((line) => line.quantity > 0)], ['remove', lines.filter((line) => line.quantity === 0)]].forEach(([action, group]) => {
        if (!group.length) return;
        publishStandardEvent('CartLinesUpdateEvent', {
          action: action,
          context: detail.context || 'cart',
          lines: group,
          promise: Promise.resolve({ cart: standardEventCart(detail.cart) })
        });
      });
      return;
    }
    if (!detail.key) return;
    const quantity = parseInt(detail.quantity, 10) || 0;
    publishStandardEvent('CartLinesUpdateEvent', {
      action: quantity === 0 ? 'remove' : 'update',
      context: detail.context || 'cart',
      lines: [{ id: detail.key, quantity: quantity }],
      promise: Promise.resolve({ cart: standardEventCart(detail.cart) })
    });
  });

  // shopify:cart:error — add-to-cart rejected (out of stock, limits, etc.)
  document.addEventListener('product:variant-error', (e) => {
    const detail = e.detail || {};
    publishStandardEvent('CartErrorEvent', standardEventCartError(detail.errors || detail.message));
  });
})();

const OverlayManager = {
  stack: [],
  // <modal-dialog> layers (modal-dialog.js). They sit in <body>, outside
  // #wrapper, and always stack above any open side panel.
  modals: [],
  beforeCloseHandlers: new Map(),
  // Panels whose close has started but waits on a beforeClose transition.
  closing: new WeakSet(),
  returnFocus: new WeakMap(),
  // Elements outside a panel that its focus trap also cycles through.
  trapExtras: new WeakMap(),
  // Panels that close when another opens (opts.givesWay).
  givesWay: new WeakSet(),

  // opts.include: elements outside the panel that stay in its focus trap,
  // eg. the mobile menu's header toggle, which doubles as its close button.
  // opts.givesWay: the panel closes when another opens from outside it, eg.
  // the mobile menu, whose header stays usable above it: search and the cart
  // open from there, and two drawers at once would cover each other. Quick
  // view from a product in the menu opens from inside, and stacks over it.
  // opts.opener: the control that opened the panel, when the caller knows
  // it. A tap doesn't focus it in Safari, so focus alone can't tell.
  open(panel, opts = {}) {
    if (!panel || this.stack.includes(panel)) return;
    const origin = opts.opener || document.activeElement;
    const replaced = this.stack.filter((el) => this.givesWay.has(el) && !el.contains(origin));
    // Opened from inside a panel on its way out (quick view's add to cart
    // opens the cart drawer as the quick view closes), focus goes back to
    // that panel's opener: the panel's content is about to go. The same when
    // that would be inside a panel giving way.
    const leaving = this.stack.find((el) => this.closing.has(el) && el.contains(origin));
    let returnTo = leaving ? this.returnFocus.get(leaving) : origin;
    const gone = replaced.find((el) => el.contains(returnTo));
    if (gone) returnTo = this.returnFocus.get(gone);
    this.stack.push(panel);
    this.returnFocus.set(panel, returnTo);
    if (opts.include) this.trapExtras.set(panel, opts.include);
    if (opts.givesWay) this.givesWay.add(panel);
    panel.classList.add('active');
    panel.inert = false;
    document.body.classList.add('open-cc');
    // Focus the close button, or the panel itself when it has none showing.
    const close = panel.querySelector('.side-panel-close__button');
    close?.focus({ preventScroll: true });
    if (!close || document.activeElement !== close) panel.focus({ preventScroll: true });
    // Once focus is in the new panel, so the old one doesn't take it back.
    replaced.forEach((el) => this.close(el));
    dispatchCustomEvent('panel:open', { panel });
  },

  close(panel) {
    if (!panel) return;
    this.closing.add(panel);
    const handler = this.beforeCloseHandlers.get(panel);
    if (handler) {
      handler(() => this._close(panel));
    } else {
      this._close(panel);
    }
  },

  _close(panel) {
    this.closing.delete(panel);
    const index = this.stack.indexOf(panel);
    if (index > -1) this.stack.splice(index, 1);
    panel.classList.remove('active');
    panel.inert = true;
    if (!this.stack.length) {
      document.body.classList.remove('open-cc');
    }
    // Return focus to the element that opened the overlay — but only when
    // focus is still inside the panel (or lost to <body>), so we never
    // steal focus the user has already moved elsewhere.
    const opener = this.returnFocus.get(panel);
    if (opener && document.contains(opener) && (panel.contains(document.activeElement) || document.activeElement === document.body)) {
      opener.focus();
    }
    this.returnFocus.delete(panel);
    this.trapExtras.delete(panel);
    this.givesWay.delete(panel);
    dispatchCustomEvent('panel:closed', { panel });
  },

  closeLast() {
    const panel = this.stack[this.stack.length - 1];
    if (panel) this.close(panel);
  },

  // A modal covers the whole page, so everything in #wrapper (side panels
  // included) goes inert behind it. Not in the theme editor, where the
  // modal is not moved out of #wrapper and would turn inert itself.
  openModal(modal, opener) {
    if (!modal || !modal.isConnected || this.modals.includes(modal)) return;
    this.modals.push(modal);
    this.returnFocus.set(modal, opener || document.activeElement);
    const wrapper = document.getElementById('wrapper');
    if (wrapper && !window.Shopify?.designMode) wrapper.inert = true;
  },

  closeModal(modal) {
    const index = this.modals.indexOf(modal);
    if (index === -1) return;
    this.modals.splice(index, 1);
    if (!this.modals.length) {
      const wrapper = document.getElementById('wrapper');
      if (wrapper) wrapper.inert = false;
    }
    // Same rule as panels: only take focus back while it is still inside
    // the modal, or was lost to <body>.
    const opener = this.returnFocus.get(modal);
    if (opener && document.contains(opener) && (modal.contains(document.activeElement) || document.activeElement === document.body)) {
      opener.focus();
    }
    this.returnFocus.delete(modal);
  },

  // The layer that owns focus and Escape: the last modal, else the last panel.
  top() {
    return this.modals[this.modals.length - 1] || this.stack[this.stack.length - 1];
  },

  // A key pressed inside a native <dialog> or popover, which handles Tab
  // and Escape itself: eg. the Shopify account sheet (a <dialog> in a
  // shadow root) opened from the mobile menu.
  inNativeLayer(e) {
    const popovers = 'popover' in HTMLElement.prototype;
    return e.composedPath().some((node) => (node instanceof HTMLDialogElement && node.open) || (popovers && node instanceof HTMLElement && node.matches(':popover-open')));
  },

  // Escape closes the top layer only. An age gate ([disabled]) cannot be
  // dismissed, so Escape does nothing while it is open. Escape in a native
  // layer is left to it: it would otherwise close the panel under it too.
  onEscape(e) {
    if (e.defaultPrevented || e.isComposing || this.inNativeLayer(e)) return;
    const modal = this.modals[this.modals.length - 1];
    if (modal) {
      if (!modal.hasAttribute('disabled')) modal.hide();
      return;
    }
    this.closeLast();
  },

  registerBeforeClose(panel, handler) {
    this.beforeCloseHandlers.set(panel, handler);
  },

  isOpen(panel) {
    return this.stack.includes(panel);
  },

  hasOpen() {
    return this.stack.length > 0;
  },

  // Keep Tab/Shift+Tab cycling inside the top-most open overlay. One
  // document-level listener covers every drawer/modal the manager opens.
  trapFocus(e) {
    if (e.key !== 'Tab') return;
    const top = this.top();
    if (!top || this.inNativeLayer(e)) return;
    const scope = [...(this.trapExtras.get(top) || []), top];
    const selector = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, iframe, [tabindex]:not([tabindex="-1"])';
    // checkVisibility() also sees what the browser skips while it still has
    // boxes: the content of a closed <details> (Chrome hides it with
    // content-visibility) and visibility: hidden, as on a closed submenu.
    const visible = (el) => (el.checkVisibility ? el.checkVisibility({ visibilityProperty: true, checkVisibilityCSS: true }) : el.getClientRects().length > 0);
    // Controls in an open shadow root (eg. wallet buttons) count through
    // their host: it takes their place in the order, and focusing it means
    // focusing its first control (Shift+Tab: its last).
    const shadowed = (el) => (el.shadowRoot ? Array.from(el.shadowRoot.querySelectorAll(selector)).filter(visible) : []);
    const focusables = scope
      .flatMap((root) => [root, ...root.querySelectorAll('*')])
      .filter((el) => (el.matches(selector) || shadowed(el).length) && !el.closest('[inert]') && visible(el));
    if (scope.length > 1) {
      focusables.sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1));
    }

    if (!focusables.length) {
      e.preventDefault();
      return;
    }

    const active = document.activeElement;
    const root = scope.find((el) => el.contains(active));
    let target;

    // Focus inside a shadow root: the browser moves through it, so step in
    // only from the last of its controls (Shift+Tab: the first). A closed
    // shadow root can't be looked into, so it falls through to the rules
    // below.
    const deep = e.composedPath()[0];
    if (root && deep !== active && active.shadowRoot) {
      const inner = shadowed(active);
      if (deep !== (e.shiftKey ? inner[0] : inner[inner.length - 1])) return;
    }

    if (root) {
      const index = focusables.indexOf(active);
      if (index > -1) {
        target = focusables[index + (e.shiftKey ? -1 : 1)];
      } else {
        // Focus rests on a container, such as a panel or dialog focused on
        // open: the nearest tabbable element in the direction of travel.
        const direction = e.shiftKey ? Node.DOCUMENT_POSITION_PRECEDING : Node.DOCUMENT_POSITION_FOLLOWING;
        const ahead = focusables.filter((el) => active.compareDocumentPosition(el) & direction);
        target = e.shiftKey ? ahead[ahead.length - 1] : ahead[0];
      }
      // Within one root the browser moves focus itself. Step in only to
      // wrap around the ends, or to cross between roots, which are not
      // adjacent in the DOM (the mobile toggle sits in the header).
      if (target && root.contains(target)) return;
    }

    e.preventDefault();
    const next = target || (e.shiftKey ? focusables[focusables.length - 1] : focusables[0]);
    const inner = next.matches(selector) ? [] : shadowed(next);
    (inner.length ? inner[e.shiftKey ? inner.length - 1 : 0] : next).focus();
  }
};
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') {
    OverlayManager.onEscape(e);
  } else {
    OverlayManager.trapFocus(e);
  }
});

/**
 *  @class
 *  @function Quantity
 */
if (!customElements.get('quantity-selector')) {
  class QuantityInput extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('.qty');
      this.step = this.input.getAttribute('step');
      this.changeEvent = new Event('change', {
        bubbles: true
      });
      // Create buttons
      this.subtract = this.querySelector('.minus');
      this.add = this.querySelector('.plus');

      // Add functionality to buttons
      this.subtract.addEventListener('click', () => this.change_quantity(-1 * this.step));
      this.add.addEventListener('click', () => this.change_quantity(1 * this.step));

    }
    connectedCallback() {
      this.classList.add('buttons_added');
      this.validateQtyRules();
    }
    change_quantity(change) {
      // Get current value
      let quantity = Number(this.input.value);

      // Ensure quantity is a valid number
      if (isNaN(quantity)) quantity = 1;

      // Check for min & max
      if (this.input.getAttribute('min') > (quantity + change)) {
        return;
      }
      if (this.input.getAttribute('max')) {
        if (this.input.getAttribute('max') < (quantity + change)) {
          return;
        }
      }
      // Change quantity
      quantity += change;

      // Never below the input's min, or 1 without one (the quick order
      // list's cells go down to 0, to remove).
      quantity = Math.max(quantity, this.input.hasAttribute('min') ? Number(this.input.getAttribute('min')) : 1);

      // Output number
      this.input.value = quantity;

      this.input.dispatchEvent(this.changeEvent);

      this.validateQtyRules();
    }
    validateQtyRules() {
      const value = parseInt(this.input.value);
      if (this.input.min) {
        const min = parseInt(this.input.min);
        this.subtract.classList.toggle('disabled', value <= min);
      }
      if (this.input.max) {
        const max = parseInt(this.input.max);
        this.add.classList.toggle('disabled', value >= max);
      }
    }
  }
  customElements.define('quantity-selector', QuantityInput);
}

/**
 *  @class
 *  @function ProductCard
 */
if (!customElements.get('product-card')) {
  // Reaching a card by either of these fetches its hover and swatch images.
  const PRODUCT_CARD_PRELOAD_EVENTS = ['pointerenter', 'focusin'];

  class ProductCard extends HTMLElement {
    constructor() {
      super();
      this._observer = null;
    }
    connectedCallback() {
      this.swatches = this.querySelector('.product-card-swatches');
      this.image = this.querySelector('.product-card--featured-image-link .product-primary-image');
      this.additional_images = this.querySelectorAll('.product-secondary-image');
      this.additional_images_nav = this.querySelectorAll('.product-secondary-images-nav li');
      this.quick_add = this.querySelector('.product-card--add-to-cart-button-simple');
      this.size_options = this.querySelector('.product-card-sizes');

      if (this.swatches) {
        this.enableSwatches(this.swatches, this.image);
      }
      if (this.additional_images) {
        this.enableAdditionalImages();
      }
      if (this.quick_add) {
        this.quick_add.addEventListener('click', this.quickAdd.bind(this));
      }
      if (this.size_options) {
        this.enableSizeOptions();
      }
      if (this.additional_images.length || this.swatches) {
        this.preloadWhenNeeded();
      }
      this.observeVideo();
    }
    disconnectedCallback() {
      if (this._observer) {
        this._observer.disconnect();
        this._observer = null;
      }
      if (this._preloadObserver) {
        this._preloadObserver.disconnect();
        this._preloadObserver = null;
      }
    }
    // The hover and swatch images are fetched once the card is about to be
    // used: when a mouse, pen or focus first reaches it, or, where the
    // device can hover, once it comes near the viewport after the page has
    // loaded. Fetching them for every card at load cost hundreds of KB for
    // cards far below the fold, such as product recommendations. Touch is
    // left out: it never shows the hover images, and pointerenter fires as
    // a finger lands, whether it then taps or only scrolls past.
    preloadWhenNeeded() {
      if (this._preloaded) return;
      if (!this._preloadHandler) {
        this._preloadHandler = (event) => {
          if (event.pointerType === 'touch') return;
          this.preloadImages();
        };
        PRODUCT_CARD_PRELOAD_EVENTS.forEach((type) => {
          this.addEventListener(type, this._preloadHandler, { passive: true });
        });
      }
      if (!window.matchMedia('(hover: hover)').matches) return;

      const observe = () => {
        if (this._preloaded || this._preloadObserver || !this.isConnected) return;
        this._preloadObserver = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.preloadImages();
        }, { rootMargin: '200px' });
        this._preloadObserver.observe(this);
      };
      if (document.readyState === 'complete') {
        observe();
      } else {
        window.addEventListener('load', observe, { once: true });
      }
    }
    preloadImages() {
      if (this._preloaded) return;
      this._preloaded = true;
      if (this._preloadHandler) {
        PRODUCT_CARD_PRELOAD_EVENTS.forEach((type) => {
          this.removeEventListener(type, this._preloadHandler);
        });
      }
      if (this._preloadObserver) {
        this._preloadObserver.disconnect();
        this._preloadObserver = null;
      }
      // At the width the card image is drawn, so the hover and swatch
      // images fetched are the ones the card picks when it shows them.
      const width = this.image ? Math.round(this.image.getBoundingClientRect().width) : 0;
      this.additional_images.forEach((image) => {
        theme.loadNow(image, width);
      });
      if (this.swatches) {
        this.swatches.querySelectorAll('.product-card-swatch').forEach((swatch) => {
          if (!swatch.dataset.srcset) return;
          let img = new Image();
          if (width) img.sizes = width + 'px';
          img.srcset = swatch.dataset.srcset;
        });
      }
    }
    observeVideo() {
      var template = this.querySelector('.product-card__video-deferred');
      if (!template) return;

      var self = this;
      this._observer = new IntersectionObserver(function(entries) {
        if (entries[0].isIntersecting) {
          self._observer.disconnect();
          self._observer = null;
          // Above the fold the card shows the poster as an eager image
          // (product-card.liquid); the video reuses that file as its poster
          // and for the fallback image video_tag puts inside it.
          var content = template.content.cloneNode(true),
            poster = self.querySelector('.product-card__video-poster'),
            video = content.querySelector('video');
          if (poster && video && poster.currentSrc) {
            video.setAttribute('poster', poster.currentSrc);
            video.querySelectorAll('img').forEach(function(img) {
              img.setAttribute('src', poster.currentSrc);
            });
          }
          template.replaceWith(content);
        }
      }, { rootMargin: '200px' });

      this._observer.observe(this);
    }
    enableAdditionalImages() {
      let image_length = this.additional_images.length;
      let images = this.additional_images;
      let nav = this.additional_images_nav;
      let hasNav = nav.length > 0;
      let image_container = this.querySelector('.product-card--featured-image-link');
      let lastIndex = -1;

      const mousemove = function (e) {
        let l = e.offsetX;
        let w = this.getBoundingClientRect().width;
        let sel = Math.floor((l / w) * image_length);

        if (sel === lastIndex || !images[sel]) {
          return;
        }
        if (lastIndex >= 0 && images[lastIndex]) {
          images[lastIndex].classList.remove('hover');
          if (hasNav) {
            nav[lastIndex].classList.remove('active');
          }
        }
        images[sel].classList.add('hover');
        if (hasNav) {
          nav[sel].classList.add('active');
        }
        lastIndex = sel;
      };

      const mouseleave = function () {
        if (lastIndex >= 0 && images[lastIndex]) {
          images[lastIndex].classList.remove('hover');
          if (hasNav) {
            nav[lastIndex].classList.remove('active');
          }
        }
        lastIndex = -1;
      };

      if (image_container) {
        image_container.addEventListener('touchstart', mousemove, {
          passive: true
        });
        image_container.addEventListener('touchmove', mousemove, {
          passive: true
        });
        image_container.addEventListener('touchend', mouseleave, {
          passive: true
        });
        image_container.addEventListener('mouseenter', mousemove, {
          passive: true
        });
        image_container.addEventListener('mousemove', mousemove, {
          passive: true
        });
        image_container.addEventListener('mouseleave', mouseleave, {
          passive: true
        });
      }
    }
    enableSwatches(swatches, image) {
      // Under a colour filter the card also names the colour it shows
      // (color_name).
      let swatch_list = swatches.querySelectorAll('.product-card-swatch'),
        org_srcset = image ? (image.getAttribute('srcset') || '') : '',
        color_name = this.querySelector('[data-card-color]');
      this.color_index = this.swatches.dataset.index;

      // Each swatch links to its colour (product-card-swatch.liquid). Hover
      // or keyboard focus previews it.
      swatch_list.forEach((swatch, index) => {
        const preview = () => {
          swatch_list.forEach((el) => {
            el.classList.remove('active');
          });
          if (image) {
            if (swatch.dataset.srcset) {
              image.setAttribute('srcset', swatch.dataset.srcset);
            } else {
              image.setAttribute('srcset', org_srcset);
            }
          }
          if (this.size_options) {
            // Combined listings: a colour of another product of the
            // listing (data-product-url) has none of this card's sizes,
            // so they are hidden while it shows.
            const sizes = this.size_options.parentElement;
            const sibling = !!swatch.dataset.productUrl;
            if (sibling || sizes.style.visibility) sizes.style.visibility = sibling ? 'hidden' : '';
            if (!sibling) {
              this.current_options[this.color_index] = swatch.querySelector('span').innerText;
              this.updateMasterId();
            }
          }
          // The image now shows this swatch's colour, so the name follows.
          if (color_name) {
            color_name.textContent = swatch.querySelector('span').textContent;
          }
          swatch.classList.add('active');
        };
        swatch.addEventListener('mouseover', preview);
        swatch.addEventListener('focus', preview);
      });
    }
    enableSizeOptions() {
      let size_list = this.size_options.querySelectorAll('.product-card-sizes--size'),
        featured_image = this.querySelector('.product-card--featured-image'),
        has_hover = featured_image.classList.contains('thb-hover'),
        size_parent = this.size_options.parentElement;

      this.size_index = this.size_options.dataset.index;

      this.current_options = this.size_options.dataset.options.split(',');

      this.updateMasterId();

      size_parent.addEventListener('mouseenter', () => {
        if (has_hover) {
          featured_image.classList.remove('thb-hover');
        }
      }, {
        passive: true
      });
      size_parent.addEventListener('mouseleave', () => {
        if (has_hover) {
          featured_image.classList.add('thb-hover');
        }
      }, {
        passive: true
      });
      size_list.forEach((size) => {
        size.addEventListener('click', (evt) => {
          evt.preventDefault();
          if (size.classList.contains('is-disabled')) {
            return;
          }
          this.current_options[this.size_index] = size.querySelector('span').innerText;
          this.updateMasterId();
          // No variant has this size in the colour shown, eg. a colour
          // swatch of another product of a combined listing: setDisabled
          // is about to disable it.
          if (!this.currentVariant) return;
          this.addToCart(this.currentVariant.id, size);
        });
      });
    }
    updateMasterId() {
      this.currentVariant = this.getVariantData().find((variant) => {
        return !variant.options.map((option, index) => {
          return this.current_options[index] === option;
        }).includes(false);
      });
      setTimeout(() => {
        this.setDisabled();
      }, 100);
    }
    getVariantData() {
      this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
      return this.variantData;
    }
    setDisabled() {
      const variant_data = this.getVariantData();
      if (!variant_data) {
        return true;
      }
      const size_buttons = this.size_options.querySelectorAll('.product-card-sizes--size');

      if (this.currentVariant) {
        const selected_options = this.currentVariant.options.map((value, index) => ({
          value,
          index: `option${index + 1}`
        }));
        const available_options = this.createAvailableOptionsTree(variant_data, selected_options);
        const fieldset_options = Object.values(available_options)[this.size_index];
        if (fieldset_options) {
          size_buttons.forEach((input, input_i) => {
            this.setSizeDisabled(input, fieldset_options[input_i].isUnavailable);
          });
        }
      } else {
        size_buttons.forEach((input) => {
          this.setSizeDisabled(input, true);
        });
      }
      return true;
    }
    // Unavailable sizes stay focusable, so they can still be found, and are
    // announced as disabled; the click handler ignores them. A size that is
    // adding to cart keeps the aria-disabled addToCart gave it.
    setSizeDisabled(size, disabled) {
      size.classList.toggle('is-disabled', disabled);
      if (disabled) {
        size.setAttribute('aria-disabled', 'true');
      } else if (!size.classList.contains('loading')) {
        size.removeAttribute('aria-disabled');
      }
    }
    createAvailableOptionsTree(variant_data, selected_options) {
      // Reduce variant array into option availability tree
      return variant_data.reduce((options, variant) => {

        // Check each option group (e.g. option1, option2, option3) of the variant
        Object.keys(options).forEach(index => {

          if (variant[index] === null) return;

          let entry = options[index].find(option => option.value === variant[index]);

          if (typeof entry === 'undefined') {
            // If option has yet to be added to the options tree, add it
            entry = {
              value: variant[index],
              isUnavailable: true
            };
            options[index].push(entry);
          }

          // Check how many selected option values match a variant
          const countVariantOptionsThatMatchCurrent = selected_options.reduce((count, {
            value,
            index
          }) => {
            return variant[index] === value ? count + 1 : count;
          }, 0);

          // Only enable an option if an available variant matches all but one current selected value
          if (countVariantOptionsThatMatchCurrent >= selected_options.length - 1) {
            entry.isUnavailable = entry.isUnavailable && variant.available ? false : entry.isUnavailable;
          }

          // Make sure if a variant is unavailable, disable currently selected option
          if ((!this.currentVariant || !this.currentVariant.available) && selected_options.find((option) => option.value === entry.value && index === option.index)) {
            entry.isUnavailable = true;
          }

          // First option is always enabled
          if (index === 'option1') {
            entry.isUnavailable = entry.isUnavailable && variant.available ? false : entry.isUnavailable;
          }
        });

        return options;
      }, {
        option1: [],
        option2: [],
        option3: []
      });
    }
    addToCart(variantId, button) {
      button.classList.add('loading');
      button.setAttribute('aria-disabled', true);

      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', 1);
      formData.append('sections', this.getSectionsToRender().map((section) => section.section));
      formData.append('sections_url', window.location.pathname);
      // Its place among the cart's redraws (theme.cart.draws).
      const sequence = theme.cart.claim();

      fetch(`${theme.routes.cart_add_url}`, {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/javascript'
        },
        body: formData
      })
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            return;
          }
          this.renderContents(response, sequence);
          dispatchCustomEvent('cart:item-added', {
            product: response.hasOwnProperty('items') ? response.items[0] : response,
            quantity: 1,
            context: 'product'
          });
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          button.classList.remove('loading');
          if (!button.classList.contains('is-disabled')) button.removeAttribute('aria-disabled');
        });
    }
    quickAdd(evt) {
      evt.preventDefault();
      if (this.quick_add.disabled) {
        return;
      }
      this.addToCart(this.quick_add.dataset.productId, this.quick_add);
    }
    getSectionsToRender() {
      return [{
        id: 'Cart',
        section: 'main-cart',
        selector: '.thb-cart-form'
      },
      {
        id: 'Cart-Drawer',
        section: 'cart-drawer',
        selector: '.cart-drawer'
      },
      {
        id: 'cart-drawer-toggle',
        section: 'cart-bubble',
        selector: '.thb-item-count'
      }];
    }
    // A newer cart redraw already drawn (theme.cart.draws) keeps the cart.
    renderContents(parsedState, sequence) {
      if (!sequence || theme.cart.draws(sequence)) {
        this.getSectionsToRender().forEach((section => {
          if (!document.getElementById(section.id)) {
            return;
          }
          const elementToReplace = document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
          elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);

        }));
      }


      const cartDrawer = document.getElementById('Cart-Drawer');
      if (cartDrawer) {
        OverlayManager.open(cartDrawer);
        dispatchCustomEvent('cart-drawer:open');
      }
    }
    getSectionInnerHTML(html, selector = '.shopify-section') {
      return new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(selector).innerHTML;
    }
  }
  customElements.define('product-card', ProductCard);
}


/**
 *  @function afterTransition
 *  Runs callback once element's own transition has finished: on its
 *  transitionend, or its transitioncancel with nothing left running (a
 *  transition still in its delay when it was undone never ends), or after
 *  `timeout` ms when none runs at all (reduced motion, nothing to animate).
 */
var afterTransition = function afterTransition(element, callback, timeout = 800) {
  let timer = 0;
  const finish = (event) => {
    if (event && event.target !== element) return;
    // Undone mid-way: the way back is a new transition, which ends later.
    if (event && event.type === 'transitioncancel' && typeof element.getAnimations === 'function' && element.getAnimations().length) return;
    clearTimeout(timer);
    element.removeEventListener('transitionend', finish);
    element.removeEventListener('transitioncancel', finish);
    callback();
  };
  element.addEventListener('transitionend', finish);
  element.addEventListener('transitioncancel', finish);
  timer = setTimeout(finish, timeout);
};

/**
 *  @class
 *  @function PanelClose
 */
if (!customElements.get('side-panel-close')) {
  class PanelClose extends HTMLElement {
    connectedCallback() {
      this.cc = document.querySelector('.click-capture');

      this.onClick = () => {
        OverlayManager.closeLast();
      };

      this.addEventListener('click', this.onClick);

      if (!this.cc.hasAttribute('initialized')) {
        this.cc.addEventListener('click', this.onClick);
        this.cc.setAttribute('initialized', '');
      }
    }
  }
  customElements.define('side-panel-close', PanelClose);

  // Product drawer: close images container first, then drawer
  const productDrawer = document.getElementById('Product-Drawer');
  if (productDrawer) {
    OverlayManager.registerBeforeClose(productDrawer, (done) => {
      const container = productDrawer.querySelector('.product-quick-images--container');
      if (container && container.classList.contains('active') && window.innerWidth >= 1069) {
        container.classList.remove('active');
        afterTransition(container, () => {
          productDrawer.querySelector('#Product-Drawer-Content').innerHTML = '';
          done();
        });
      } else {
        container?.classList.remove('active');
        productDrawer.querySelector('#Product-Drawer-Content').innerHTML = '';
        done();
      }
    });

    document.addEventListener('panel:open', (e) => {
      if (e.detail.panel === productDrawer) {
        setTimeout(() => {
          productDrawer.querySelector('.product-quick-images--container')?.classList.add('active');
        });
      }
    });
  }
}
/**
 *  @function CartFocus
 *  Cart redraws (the drawer, and the cart page in cart.js) replace their
 *  markup through innerHTML, which drops keyboard focus on <body>.
 *  capture() notes the focused element: its id, or tag and classes, and its
 *  place among the matches, inside its line when it sits in one (the line
 *  by item key, then position). restore() focuses the same element in the
 *  new markup: on that line, else the line now in that position, else the
 *  last line; outside the lines, in the root; failing that, `fallback`.
 */
const CartFocus = {
  selector: 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])',
  // The cart page renders mobile and desktop copies of some controls.
  visible(elements) {
    return Array.from(elements).filter((el) => el.getClientRects().length > 0);
  },
  signature(el) {
    if (el.id) return `#${CSS.escape(el.id)}`;
    return el.localName + Array.from(el.classList, (name) => `.${CSS.escape(name)}`).join('');
  },
  lineKey(line) {
    return line.querySelector('.qty')?.dataset.key;
  },
  capture(root, lineSelector) {
    const active = document.activeElement;
    if (!active || active === root || !root.contains(active)) return null;
    const lines = Array.from(root.querySelectorAll(lineSelector));
    const line = lines.find((el) => el.contains(active));
    const scope = line || root;
    const signature = this.signature(active);
    return {
      key: line && this.lineKey(line),
      position: lines.indexOf(line),
      signature,
      match: this.visible(scope.querySelectorAll(signature)).indexOf(active),
      index: this.visible(scope.querySelectorAll(this.selector)).indexOf(active)
    };
  },
  restore(root, lineSelector, state, fallback) {
    const active = document.activeElement;
    if (!state || (active && active !== document.body)) return;
    let scope = root;
    if (state.position > -1) {
      const lines = Array.from(root.querySelectorAll(lineSelector));
      scope = lines.find((el) => this.lineKey(el) === state.key) || lines[state.position] || lines[lines.length - 1];
    }
    let target;
    if (scope) {
      const matches = this.visible(scope.querySelectorAll(state.signature));
      target = matches[state.match] || matches[0] || this.visible(scope.querySelectorAll(this.selector))[state.index];
    }
    // A control the redraw folded away (a closed <details>, which still
    // has boxes): its summary instead.
    const folded = target && target.closest('details:not([open])');
    if (folded && !target.matches('summary')) target = folded.querySelector('summary');
    (target || fallback)?.focus();
  }
};

/**
 *  @function theme.money
 *  Cents → text in the cart's currency, with the shop's money format (its
 *  currency-code format while "Show currency codes" is on), as Liquid's
 *  money filters print it: theme.money(1850) → "$18.50". For amounts the
 *  theme works out in the browser. `format` is another money format to use
 *  (eg. theme.settings.money_with_currency_format, the shop's plain one).
 *
 *  Every amount placeholder Shopify has is known (the vendor formatMoney
 *  misses the space-separated ones), and the result is text: a format's
 *  HTML tags and entities (<span class=money>, &euro;) are resolved, as the
 *  page would show them, for textContent.
 */
theme.money = (function () {
  // Placeholder: [decimals, thousands separator, decimal separator].
  const AMOUNTS = {
    amount: [2, ',', '.'],
    amount_no_decimals: [0, ',', '.'],
    amount_with_comma_separator: [2, '.', ','],
    amount_no_decimals_with_comma_separator: [0, '.', ','],
    amount_with_apostrophe_separator: [2, '\'', '.'],
    amount_no_decimals_with_space_separator: [0, ' ', ','],
    amount_with_space_separator: [2, ' ', ','],
    amount_with_period_and_space_separator: [2, ' ', '.']
  };
  const PLACEHOLDER = /\{\{\s*(\w+)\s*\}\}/;
  const amount = (cents, decimals, thousands, decimal) => {
    const fixed = ((Math.round(Number(cents)) || 0) / 100).toFixed(decimals).split('.');
    return fixed[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`) + (fixed[1] ? decimal + fixed[1] : '');
  };
  return function (cents, format) {
    const pattern = format || theme.settings.money_format || theme.settings.money_with_currency_format || '${{amount}}';
    const match = pattern.match(PLACEHOLDER);
    const rule = (match && AMOUNTS[match[1]]) || AMOUNTS.amount;
    const text = match ? pattern.replace(PLACEHOLDER, amount(cents, rule[0], rule[1], rule[2])) : amount(cents, 2, ',', '.');
    return /[<&]/.test(text) ? new DOMParser().parseFromString(text, 'text/html').documentElement.textContent : text;
  };
})();

/**
 *  @namespace theme.cart
 *  Cart writes and redraws for the theme's newer cart features (gift wrap,
 *  rewards, upsell, line edit, bundles, quick order). The older callers
 *  keep their own code.
 *
 *  TARGETS                  [{ id, section, selector }]: the cart page
 *                           (#Cart, main-cart), the drawer (#Cart-Drawer,
 *                           cart-drawer) and the header count
 *                           (#cart-drawer-toggle, cart-bubble).
 *  sections(extra)          The names of the TARGETS on this page, then
 *                           `extra` (a name or an array of them). Throws
 *                           past 5, the Section Rendering limit.
 *  request(path, body, { sections, keepalive })
 *                           POSTs body as JSON, with `sections` (default:
 *                           sections(); false for none) and sections_url;
 *                           keepalive lets it outlive the page (pagehide).
 *                           Resolves with the Ajax answer, its rendered
 *                           sections in .sections. Rejects with an Error
 *                           carrying { status, body }: the HTTP status, or
 *                           the numeric status of an error sent with a 200,
 *                           or 0 when the server never answered (then the
 *                           cart is unchanged); body is the Ajax error JSON
 *                           { status, message, description } or null. The
 *                           shopper's text is body.description, else
 *                           theme.strings.cartError. Every failure sends one
 *                           shopify:cart:error; callers send none of their
 *                           own. Writes are never aborted.
 *  add(items, options)      /cart/add.js { items }: an array of (or one)
 *                           { id, quantity, properties, selling_plan }.
 *  update(body, options)    /cart/update.js { updates, attributes, note }.
 *  change(key, quantity, options)
 *                           /cart/change.js for the line with that key.
 *  get()                    The cart (/cart.js).
 *  render(state, { open, focusKey, fallback })
 *                           Draws state.sections into each TARGET on the
 *                           page, synchronously. A state older than the
 *                           last one drawn is skipped (returns false), and
 *                           the cart is fetched again (refresh), since the
 *                           server may have applied its request last.
 *                           Keyboard focus in the drawer or on the cart page
 *                           stays on the same control (CartFocus), else goes
 *                           to `fallback`. focusKey focuses the line with
 *                           that key instead (its first control), in the
 *                           drawer while it is open, else on the cart page.
 *                           `open` opens the drawer, except on the cart page.
 *                           The open drawer keeps its recommendations panel.
 *  refresh()                Redraws the drawer and the cart page from the
 *                           server (cart:refresh), eg. after a 422.
 *  claim(event), draws(sequence)
 *                           For the older cart redraws (the drawer's and
 *                           the cart page's steppers and refreshes,
 *                           discounts, the product form's add), so
 *                           theme.cart's states and theirs take turns in
 *                           send order: claim() when the request is sent,
 *                           then draw only if draws(sequence) is true. False
 *                           means something sent later is drawn already; the
 *                           cart is then fetched again, as for render. The
 *                           listeners of one cart:refresh event share one
 *                           number (claim(event)), so neither skips the
 *                           other.
 *  added(requested, response, context)
 *                           After an add: cart:item-added with product
 *                           (the first line added), items (the lines
 *                           /cart/add.js returned) and lines [{ key,
 *                           merchandiseId, quantity added }], each line's
 *                           quantity from the request for its variant; with
 *                           one line, also quantity. One
 *                           shopify:cart:lines-update.
 *  changed(lines, cart, context)
 *                           After a change: line-item:change:end with lines
 *                           [{ key, quantity }] (0: removed), read from
 *                           `cart` when it is a full cart; with one line,
 *                           also key and quantity. At most one update and
 *                           one remove shopify:cart:lines-update.
 *  context is 'product', 'dialog' (the drawers) or 'cart'.
 */
theme.cart = (function () {
  const TARGETS = Object.freeze([
    Object.freeze({ id: 'Cart', section: 'main-cart', selector: '.thb-cart-form' }),
    Object.freeze({ id: 'Cart-Drawer', section: 'cart-drawer', selector: '.cart-drawer' }),
    Object.freeze({ id: 'cart-drawer-toggle', section: 'cart-bubble', selector: '.thb-item-count' })
  ]);
  const LINES = '[id^="CartDrawerItem-"], [id^="CartItem-"]';
  // The sequence number of the last request sent, of the last state drawn,
  // and of each state a request resolved with.
  let sent = 0;
  let drawn = 0;
  const sequences = new WeakMap();
  // A skipped state may still hold the server's last word (it can apply
  // two requests in either order): one refresh once things settle.
  let resync = 0;
  const scheduleResync = () => {
    clearTimeout(resync);
    resync = setTimeout(() => api.refresh(), 100);
  };

  const failure = (path, status, body) => Object.assign(new Error(`Cart request failed: ${status} ${path}`), { status: status, body: body });

  const api = {
    TARGETS: TARGETS,
    sections(extra) {
      const names = TARGETS.filter((target) => document.getElementById(target.id)).map((target) => target.section);
      [].concat(extra || []).forEach((name) => {
        if (name && !names.includes(name)) names.push(name);
      });
      if (names.length > 5) throw new Error(`Section Rendering takes 5 sections at most: ${names.join(', ')}`);
      return names;
    },
    request(path, body, options = {}) {
      const sequence = ++sent;
      const sections = options.sections === undefined ? api.sections() : options.sections;
      const payload = Object.assign({}, body);
      if (sections && sections.length) {
        payload.sections = sections;
        payload.sections_url = window.location.pathname;
      }
      return themeFetch(path, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload),
        keepalive: !!options.keepalive
      })
        .then((response) => response.json().then((state) => {
          if (state && typeof state.status === 'number') throw failure(path, state.status, state);
          if (state && typeof state === 'object') sequences.set(state, sequence);
          return state;
        }, () => {
          throw failure(path, response.status, null);
        }), (error) => {
          throw failure(path, error.status || 0, error.body || null);
        })
        .catch((error) => {
          const reply = error.body || {};
          publishStandardEvent('CartErrorEvent', standardEventCartError(reply.description || reply.message || (typeof reply.errors === 'string' && reply.errors) || theme.strings.cartError));
          throw error;
        });
    },
    add(items, options) {
      return api.request(`${theme.routes.cart_add_url}.js`, { items: [].concat(items) }, options);
    },
    update(body, options) {
      return api.request(`${theme.routes.cart_update_url}.js`, body, options);
    },
    change(key, quantity, options) {
      return api.request(`${theme.routes.cart_change_url}.js`, { id: key, quantity: quantity }, options);
    },
    get() {
      return themeFetch(`${theme.routes.cart_url}.js`, { headers: { 'Accept': 'application/json' } })
        .then((response) => response.json());
    },
    render(state, options = {}) {
      const sections = state && state.sections;
      if (!sections) return false;
      const sequence = sequences.get(state);
      if (sequence && !api.draws(sequence)) return false;
      const drawer = document.getElementById('Cart-Drawer');
      const page = document.getElementById('Cart');
      const wasOpen = !!drawer && OverlayManager.isOpen(drawer) && !OverlayManager.closing.has(drawer);
      const root = [drawer, page].find((element) => element && element.contains(document.activeElement));
      const focus = root && CartFocus.capture(root, LINES);

      TARGETS.forEach((target) => {
        const element = document.getElementById(target.id);
        const html = sections[target.section];
        if (!element || typeof html !== 'string') return;
        const source = new DOMParser().parseFromString(html, 'text/html').querySelector(target.selector);
        if (source) (element.querySelector(target.selector) || element).innerHTML = source.innerHTML;
      });

      const open = !!(options.open && drawer && !page && !wasOpen);
      if (open) {
        OverlayManager.open(drawer);
        dispatchCustomEvent('cart-drawer:open');
      } else if (wasOpen) {
        drawer.querySelector('.product-recommendations--full')?.classList.add('active');
      }

      if (options.focusKey) {
        const scope = open || wasOpen ? drawer : page;
        const line = scope && Array.from(scope.querySelectorAll(LINES)).find((element) => CartFocus.lineKey(element) === options.focusKey);
        const target = line && CartFocus.visible(line.querySelectorAll(CartFocus.selector))[0];
        if (target) {
          target.focus();
          return true;
        }
      }
      if (root) {
        const fallback = options.fallback || (root === drawer ? root.querySelector('.side-panel-close__button') || root : CartFocus.visible(root.querySelectorAll(CartFocus.selector))[0]);
        CartFocus.restore(root, LINES, focus, fallback);
      }
      return true;
    },
    refresh() {
      dispatchCustomEvent('cart:refresh');
    },
    claim(event) {
      if (event) return event.cartSequence || (event.cartSequence = ++sent);
      return ++sent;
    },
    draws(sequence) {
      if (sequence < drawn) {
        scheduleResync();
        return false;
      }
      drawn = sequence;
      return true;
    },
    added(requested, response, context) {
      const asked = [].concat(requested || []).filter(Boolean);
      let items = response && (Array.isArray(response.items) ? response.items : (response.key || response.variant_id) && [response]);
      if (!items || !items.length) {
        items = asked.map((entry) => ({ id: entry.id, variant_id: entry.id, quantity: parseInt(entry.quantity, 10) || 1 }));
      }
      // Each returned line takes the request for its variant (/cart/add.js
      // need not keep the request's order); by position only when the
      // variant's request is used up (the same variant asked twice).
      const used = new Set();
      const lines = items.map((item, index) => {
        const variantId = item.variant_id || item.id;
        let ask = asked.find((entry) => !used.has(entry) && String(entry.id) === String(variantId));
        if (!ask && asked.length === items.length && !used.has(asked[index])) ask = asked[index];
        if (ask) used.add(ask);
        return {
          key: item.key,
          merchandiseId: variantId,
          quantity: ask ? parseInt(ask.quantity, 10) || 1 : item.quantity
        };
      });
      const detail = { product: items[0], items: items, lines: lines, context: context || 'product' };
      if (lines.length === 1) detail.quantity = lines[0].quantity;
      dispatchCustomEvent('cart:item-added', detail);
    },
    changed(lines, cart, context) {
      const full = !!(cart && cart.token && Array.isArray(cart.items));
      const list = [].concat(lines || []).filter((line) => line && line.key).map((line) => {
        const item = full && cart.items.find((entry) => entry.key === line.key);
        return { key: line.key, quantity: full ? (item ? item.quantity : 0) : parseInt(line.quantity, 10) || 0 };
      });
      const detail = { lines: list, cart: cart, context: context || 'cart' };
      if (list.length === 1) {
        detail.key = list[0].key;
        detail.quantity = list[0].quantity;
      }
      dispatchCustomEvent('line-item:change:end', detail);
    }
  };
  return api;
})();

/**
 *  @class
 *  @function CartDrawer
 */
if (!customElements.get('cart-drawer')) {
  class CartDrawer extends HTMLElement {

    constructor() {
      super();
    }

    connectedCallback() {
      let button = document.getElementById('cart-drawer-toggle');
      if (!button) return;

      // Add functionality to buttons
      button.addEventListener('click', (e) => {
        e.preventDefault();
        OverlayManager.open(this, { opener: button });
        dispatchCustomEvent('cart-drawer:open');
      });

      document.addEventListener('panel:open', (e) => {
        if (e.detail.panel === this) {
          setTimeout(() => {
            this.querySelector('.product-recommendations--full')?.classList.add('active');
          });
        }
      });

      this.debouncedOnChange = debounce((event) => {
        this.onChange(event);
      }, 300);

      document.addEventListener('cart:refresh', (event) => {
        this.refresh(theme.cart.claim(event));
      });

      this.addEventListener('change', this.debouncedOnChange.bind(this));

      OverlayManager.registerBeforeClose(this, (done) => {
        const recs = this.querySelector('.product-recommendations--full');
        if (recs && recs.classList.contains('active') && window.innerWidth >= 1069) {
          recs.classList.remove('active');
          afterTransition(recs, done);
        } else {
          recs?.classList.remove('active');
          done();
        }
      });

    }
    onChange(event) {
      if (event.target.classList.contains('qty')) {
        this.updateQuantity(event.target.dataset.key, event.target.value, event.target.dataset.index);
      }
    }
    getSectionsToRender() {
      return [{
        id: 'Cart-Drawer',
        section: 'cart-drawer',
        selector: '.cart-drawer'
      },
      {
        id: 'cart-drawer-toggle',
        section: 'cart-bubble',
        selector: '.thb-item-count'
      }];
    }
    getSectionInnerHTML(html, selector) {
      return new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(selector).innerHTML;
    }
    // Target the line by its item key: positions shift when apps add or
    // remove lines behind the page's back. `line` is only used for DOM ids.
    updateQuantity(key, quantity, line) {
      this.querySelector(`#CartDrawerItem-${line}`)?.classList.add('thb-loading');
      this.querySelector(`#CartDrawer-LineItemError-${line}`)?.setAttribute('hidden', '');
      this.querySelector('#CartDrawer-Error')?.setAttribute('hidden', '');
      // What the line held before this change: the input's rendered value.
      const previous = parseInt(this.querySelector(`#CartDrawerItem-${line} .qty`)?.defaultValue, 10);
      const body = JSON.stringify({
        id: key,
        quantity,
        sections: this.getSectionsToRender().map((section) => section.section),
        sections_url: window.location.pathname
      });

      dispatchCustomEvent('line-item:change:start', {
        quantity: quantity
      });
      this.querySelector('.product-recommendations--full')?.classList.remove('active');

      // Set once the server has answered: from then on the cart may have
      // changed, whatever went wrong.
      let answered = false;
      // Its place among the cart's redraws (theme.cart.draws).
      const sequence = theme.cart.claim();
      themeFetch(`${theme.routes.cart_change_url}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': `application/json`
        },
        body
      })
        .then((response) => {
          answered = true;
          return response.json();
        })
        .then((parsedState) => {
          // An error payload sent with a 200.
          if (!parsedState.sections) {
            throw Object.assign(new Error('Cart change failed'), { body: parsedState });
          }
          return parsedState;
        })
        // Only a refused or failed change takes the error path: an error
        // while redrawing an accepted one is logged and nothing more.
        .then((parsedState) => this.renderQuantityChange(key, quantity, parsedState, sequence), (error) => this.handleQuantityError(error, key, line, answered, previous))
        .catch((error) => {
          console.error(error);
          this.querySelectorAll('.thb-loading').forEach((row) => row.classList.remove('thb-loading'));
        });
    }
    // A newer redraw already drawn (theme.cart.draws) keeps the drawer; the
    // change is still reported, and the cart is fetched again.
    renderQuantityChange(key, quantity, parsedState, sequence) {
      const draw = !sequence || theme.cart.draws(sequence);
      const focus = draw && this.getFocusState();

      if (draw) {
        this.getSectionsToRender().forEach((section => {
          const elementToReplace = document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
          elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
        }));
      }

      // The change is made: reported before anything else can fail.
      const updated = (parsedState.items || []).find((item) => item.key === key);
      dispatchCustomEvent('line-item:change:end', {
        key: key,
        context: 'dialog',
        quantity: updated ? updated.quantity : quantity,
        cart: parsedState
      });

      if (!draw) return;
      this.restoreFocus(focus);

      // Accepted, but capped at what can be bought.
      if (updated && updated.quantity < parseInt(quantity, 10)) {
        this.showLineError(key, theme.strings.cartQuantityError.replace('[quantity]', updated.quantity), true);
      }
    }
    // A refused change (422, eg. more than is in stock) can still have
    // changed the line: Shopify caps it at what is available and says so.
    // So once the server has answered, the drawer is redrawn from the cart
    // as it now is, with the message on the line. When it never answered,
    // or that redraw fails too, the line goes back to the quantity it
    // showed and says the update failed. The line's line-item:change:end
    // (a standard event's update) is only sent for a change that happened.
    handleQuantityError(error, key, line, answered, previous) {
      console.error(error);
      const reply = error.body || {};
      const message = reply.description || reply.message || (typeof reply.errors === 'string' && reply.errors) || theme.strings.cartError;
      publishStandardEvent('CartErrorEvent', standardEventCartError(message));

      const keepLine = () => {
        const row = this.querySelector(`#CartDrawerItem-${line}`);
        const input = row?.querySelector('.qty');
        if (input) {
          input.value = input.defaultValue;
          input.closest('quantity-selector')?.validateQtyRules();
        }
        row?.classList.remove('thb-loading');
        this.showLineError(key, message);
        this.showRecommendations();
      };
      if (!answered && !error.status) {
        keepLine();
        return;
      }
      this.refresh().then((redrawn) => {
        if (!redrawn) {
          keepLine();
          return;
        }
        this.showLineError(key, message);
        if (error.status === 422) publishCappedCartLine(key, previous, 'dialog');
      });
    }
    // Resolves true once the drawer is redrawn, or false when the request
    // failed and the drawer was left as it was.
    // A refresh sent before a redraw already drawn (theme.cart.draws) leaves
    // the drawer to it.
    refresh(sequence = theme.cart.claim()) {
      this.querySelector('.product-recommendations--full')?.classList.remove('active');
      let sections = 'cart-drawer,cart-bubble';
      return themeFetch(`${window.location.pathname}?sections=${sections}`)
        .then((response) => response.json())
        .then((parsedState) => {
          if (!theme.cart.draws(sequence)) return true;
          const focus = this.getFocusState();

          this.getSectionsToRender().forEach((section => {
            const elementToReplace = document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);

            elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState[section.section], section.selector);
          }));
          this.restoreFocus(focus);

          // Mirror updateQuantity's completion event so listeners on the cart
          // redraw (app pixels, standard events) fire on refresh() paths too.
          // refresh() fetches section HTML only, so there is no cart payload.
          dispatchCustomEvent('line-item:change:end', {});
          return true;
        })
        .catch((error) => {
          console.error(error);
          this.querySelectorAll('.thb-loading').forEach((row) => row.classList.remove('thb-loading'));
          this.showRecommendations();
          return false;
        });
    }
    // Shows `message` on the line with this item key, or above the lines
    // when the drawer no longer has it. `html` is for the theme's own _html
    // strings; the server's messages go in as text.
    showLineError(key, message, html) {
      const row = Array.from(this.querySelectorAll('[id^="CartDrawerItem-"]')).find((el) => CartFocus.lineKey(el) === key);
      const error = row?.querySelector('.cart-item__error') || this.querySelector('#CartDrawer-Error');
      if (!error) return;
      const text = error.querySelector('.cart-item__error-text');
      if (html) {
        text.innerHTML = message;
      } else {
        text.textContent = message;
      }
      error.removeAttribute('hidden');
    }
    // A request that changed nothing gives back the recommendations panel
    // it slid away.
    showRecommendations() {
      if (OverlayManager.isOpen(this)) this.querySelector('.product-recommendations--full')?.classList.add('active');
    }
    // A redraw replaces the drawer's markup: keep keyboard focus (CartFocus),
    // falling back to the close button.
    getFocusState() {
      return CartFocus.capture(this, '[id^="CartDrawerItem-"]');
    }
    restoreFocus(state) {
      CartFocus.restore(this, '[id^="CartDrawerItem-"]', state, this.querySelector('.side-panel-close__button') || this);
    }
  }
  customElements.define('cart-drawer', CartDrawer);
}

/**
 *  @class
 *  @function ResizeSelect
 */
if (!customElements.get('resize-select')) {
  class ResizeSelect extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.select = this.querySelector('select');
      this.addEventListeners();
    }
    addEventListeners() {
      this.addEventListener('change', this.resizeSelect.bind(this));
      window.addEventListener('load', this.resizeSelect.bind(this));
      const details = this.closest('details');
      if (details) {
        details.addEventListener('toggle', () => {
          if (details.open) this.resizeSelect();
        });
      }
      this.resizeSelect();
    }
    resizeSelect() {
      let tempOption = document.createElement('option');
      tempOption.textContent = this.select.selectedOptions[0].textContent;

      let tempSelect = document.createElement('select');
      tempSelect.style.cssText = 'visibility:hidden;position:fixed;pointer-events:none';
      tempSelect.appendChild(tempOption);
      this.after(tempSelect);

      requestAnimationFrame(() => {
        if (tempSelect.clientWidth > 0) {
          this.select.style.width = `${tempSelect.clientWidth + 10}px`;
        }
        tempSelect.remove();
      });
    }
  }
  customElements.define('resize-select', ResizeSelect);
}



/**
 *  @class
 *  @function QuickView
 */
if (!customElements.get('quick-view')) {
  const quickViewCache = new Map();
  // The product drawer's title as the markup has it, while edit mode shows
  // its own.
  let defaultDrawerTitle = null;
  // Line item properties as Liquid gives them, HTML-escaped: the text
  // they stand for.
  const decodeHTML = (html) => new DOMParser().parseFromString(String(html), 'text/html').documentElement.textContent;

  /**
   *  Edit mode: a cart line's Edit link (cart-line-edit.liquid, with its
   *  data-edit-* attributes) opens the line's product to change it, and
   *  QuickView.editing holds that line while the product drawer shows it:
   *  { key, index (its row ids), quantity, variantId, properties (the
   *  line's, decoded), surface ('drawer' or 'cart': where the Edit link
   *  is) }. Every other trigger clears it, and so does the drawer closing.
   *  ProductForm (product.js) then replaces the line rather than adding to
   *  the cart. The drawer reads "Edit item", is loaded fresh rather than
   *  from the cache, adds nothing to recently viewed, and has the
   *  product-drawer--editing class, under which _product-item.scss hides
   *  Buy it now (.shopify-payment-button) and the installments banner
   *  (form.installment): either would buy or price the new choice without
   *  replacing the line.
   */
  class QuickView extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this.drawer = document.getElementById('Product-Drawer');
      this.addEventListener('click', this.handleClick.bind(this));
      // The markup gives it role="button": Enter and Space activate it.
      this.addEventListener('keydown', (e) => {
        if (e.target !== this || e.repeat || (e.key !== 'Enter' && e.key !== ' ')) return;
        e.preventDefault();
        this.click();
      });
    }
    // The cart line this trigger edits, or null for any other trigger.
    getEdit() {
      if (!this.hasAttribute('data-edit-key')) return null;
      const properties = {};
      const json = this.querySelector('script[type="application/json"]');
      try {
        const parsed = json ? JSON.parse(json.textContent) : [];
        // Liquid writes them as [name, value] pairs.
        (Array.isArray(parsed) ? parsed : Object.entries(parsed || {})).forEach((pair) => {
          if (Array.isArray(pair) && pair.length === 2 && pair[1] !== null) properties[decodeHTML(pair[0])] = decodeHTML(pair[1]);
        });
      } catch (error) {
        console.error(error);
      }
      return {
        key: this.dataset.editKey,
        index: this.dataset.editIndex,
        quantity: parseInt(this.dataset.editQuantity, 10) || 1,
        variantId: parseInt(this.dataset.variantId, 10),
        properties: properties,
        surface: this.closest('#Cart-Drawer') ? 'drawer' : 'cart'
      };
    }
    // A trigger that wraps a link, rather than acting as a button itself
    // (role="button"), leaves a modifier or non-main-button click to the
    // browser, which opens the link in a new tab or window, and follows
    // the link when the quick view can't be loaded (or names no product).
    // An Edit link does too until product.js, whose form replaces the
    // line, is on the page (product-script.liquid).
    handleClick(e) {
      const link = this.getAttribute('role') === 'button' ? null : this.querySelector('a[href]');
      if (link && (!this.dataset.productHandle || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)) return;
      const edit = this.getEdit();
      if (edit && link && !customElements.get('product-form')) return;
      e.preventDefault();
      let productHandle = this.dataset.productHandle;
      if (!productHandle) {
        return;
      }
      // A card under a variant filter passes the variant it links to.
      let variantId = this.dataset.variantId;
      let cacheKey = variantId ? `${productHandle}?variant=${variantId}` : productHandle;
      let rootUrl = theme.routes.root_url.replace(/\/+$/, '');
      let href = `${rootUrl}/products/${productHandle}?view=quick-view`;
      if (variantId) {
        href += `&variant=${encodeURIComponent(variantId)}`;
      }

      if (this.classList.contains('loading')) {
        return;
      }
      this.classList.add('loading');
      QuickView.editing = edit;

      // Abort any in-flight quick-view fetch
      if (QuickView.activeController) {
        QuickView.activeController.abort();
      }
      let controller = new AbortController();
      QuickView.activeController = controller;

      // Serve from cache if available. Editing a line shows what can be
      // bought now.
      if (!edit && quickViewCache.has(cacheKey)) {
        this.classList.remove('loading');
        this.renderQuickView(quickViewCache.get(cacheKey), href, productHandle);
        return;
      }

      QuickView.load(href, cacheKey, controller.signal, !!edit)
        .then((sectionInnerHTML) => {
          this.renderQuickView(sectionInnerHTML, href, productHandle, edit);
        }, (error) => {
          if (isAbortError(error)) return;
          console.error(error);
          if (link) window.location.assign(link.href);
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          this.classList.remove('loading');
        });
    }
    // The quick view content (the markup inside #Product-Drawer-Content)
    // of the page at url, kept under cacheKey. A variant picker in the
    // drawer loads another product of a combined listing with it too
    // (product.js). With fresh, it is fetched even when kept. An HTTP
    // error rejects with its status.
    static load(url, cacheKey, signal, fresh) {
      if (!fresh && quickViewCache.has(cacheKey)) {
        return Promise.resolve(quickViewCache.get(cacheKey));
      }
      return fetch(url, {
        method: 'GET',
        signal: signal
      })
        .then((response) => {
          if (!response.ok) {
            throw Object.assign(new Error(`Quick view fetch failed: ${response.status}`), { status: response.status });
          }
          return response.text();
        })
        .then(text => {
          let parsed = new DOMParser()
            .parseFromString(text, 'text/html')
            .querySelector('#Product-Drawer-Content');

          if (!parsed) {
            throw Object.assign(new Error('Quick view content not found in response'), { status: 200 });
          }
          let sectionInnerHTML = parsed.innerHTML;
          quickViewCache.set(cacheKey, sectionInnerHTML);
          return sectionInnerHTML;
        });
    }
    // edit: the cart line this opening edits (getEdit), or null.
    renderQuickView(sectionInnerHTML, href, productHandle, edit = null) {
      if (!sectionInnerHTML) {
        return;
      }
      QuickView.editing = edit;
      let drawerContent = this.drawer.querySelector('#Product-Drawer-Content');
      replaceProductContent(drawerContent, sectionInnerHTML);
      QuickView.showEditing(this.drawer, !!edit);
      if (edit) QuickView.applyEdit(drawerContent, { quantity: edit.quantity, properties: true });

      OverlayManager.open(this.drawer);

      dispatchCustomEvent('quick-view:open', {
        productUrl: href,
        productHandle: productHandle
      });
      // shopify:product:view for the product the drawer shows, from its
      // picker (product.js), as a combined listing switch inside it sends.
      const picker = drawerContent.querySelector('variant-selects:not([data-sticky])');
      if (picker) {
        customElements.whenDefined('variant-selects').then(() => {
          if (picker.isConnected && picker.publishProductView) picker.publishProductView('dialog');
        });
      }
      if (!edit) addIdToRecentlyViewed(productHandle);
    }
    // The cart line being edited, for markup in the product drawer (eg.
    // its product form); null anywhere else, or when nothing is edited.
    static editingFor(element) {
      return QuickView.editing && element && element.closest && element.closest('#Product-Drawer') ? QuickView.editing : null;
    }
    // The product drawer's edit mode look: its class and title.
    static showEditing(drawer, editing) {
      if (!drawer) return;
      drawer.classList.toggle('product-drawer--editing', editing);
      const title = drawer.querySelector('#Product-Drawer-Title');
      if (!title) return;
      if (defaultDrawerTitle === null) defaultDrawerTitle = title.textContent;
      title.textContent = editing ? (theme.strings.editItem || 'Edit item') : defaultDrawerTitle;
    }
    // Puts the edited line into the product markup in container (the
    // drawer's content): the quantity, and with properties, the line's
    // values in the form's line item property fields. The add button,
    // when it can be used, reads "Update". product.js calls it again after
    // a variant change or a combined listing switch re-renders them.
    static applyEdit(container, options = {}) {
      const edit = QuickView.editing;
      if (!edit || !container) return;
      const quantity = container.querySelector('input[name="quantity"]');
      if (quantity && options.quantity) {
        quantity.value = options.quantity;
        const selector = quantity.closest('quantity-selector');
        if (selector && selector.validateQtyRules) selector.validateQtyRules();
      }
      const form = container.querySelector('form[data-type="add-to-cart-form"]');
      if (options.properties && form) {
        Array.from(form.elements).forEach((field) => {
          const match = /^properties\[(.+)\]$/.exec(field.name || '');
          if (!match || field.type === 'hidden' || field.type === 'file' || !Object.prototype.hasOwnProperty.call(edit.properties, match[1])) return;
          const value = edit.properties[match[1]];
          if (field.type === 'checkbox' || field.type === 'radio') {
            field.checked = field.value === value;
          } else if (field.tagName !== 'SELECT' || Array.from(field.options).some((option) => option.value === value)) {
            field.value = value;
          }
        });
      }
      container.querySelectorAll('.single-add-to-cart-button:not([disabled]) .single-add-to-cart-button--text').forEach((text) => {
        text.textContent = theme.variantStrings.update || 'Update';
      });
    }
    static clearEdit() {
      QuickView.editing = null;
      QuickView.showEditing(document.getElementById('Product-Drawer'), false);
    }
  }
  QuickView.activeController = null;
  QuickView.editing = null;
  customElements.define('quick-view', QuickView);

  // Closing the product drawer ends an edit.
  document.addEventListener('panel:closed', (e) => {
    if (e.detail && e.detail.panel && e.detail.panel.id === 'Product-Drawer') QuickView.clearEdit();
  });
}

/**
 *  @class
 *  @function SidePanelContentTabs
 */
if (!customElements.get('side-panel-content-tabs')) {
  class SidePanelContentTabs extends HTMLElement {
    constructor() {
      super();
      this.buttons = this.querySelectorAll('button');
      this.panels = this.parentElement.querySelectorAll('.side-panel-content--tab-panel');
    }
    connectedCallback() {
      this.setupButtonObservers();
    }
    setupButtonObservers() {
      this.buttons.forEach((item, i) => {
        item.addEventListener('click', (e) => {
          this.toggleActiveClass(i);
        });
      });
    }
    toggleActiveClass(i) {
      this.buttons.forEach((button) => {
        button.classList.remove('tab-active');
      });
      this.buttons[i].classList.add('tab-active');

      this.panels.forEach((panel) => {
        panel.classList.remove('tab-active');
      });
      this.panels[i].classList.add('tab-active');
    }
  }

  customElements.define('side-panel-content-tabs', SidePanelContentTabs);
}

/**
 *  @class
 *  @function CollapsibleRow
 */
if (!customElements.get('collapsible-row')) {
  // https://css-tricks.com/how-to-animate-the-details-element/
  class CollapsibleRow extends HTMLElement {
    constructor() {
      super();

      this.details = this.querySelector('details');
      this.summary = this.querySelector('summary');
      this.content = this.querySelector('.collapsible__content');

      // Store the animation object (so we can cancel it if needed)
      this.animation = null;
      // Store if the element is closing
      this.isClosing = false;
      // Store if the element is expanding
      this.isExpanding = false;
    }
    connectedCallback() {
      this.setListeners();
    }
    setListeners() {
      // Opt-out of the WAAPI height animation. Used by horizontal facet
      // dropdowns, where the panel is floating (position: absolute) and
      // animating the parent <details> height would push surrounding layout.
      if (this.dataset.skipCollapse === 'true') return;
      this.querySelector('summary').addEventListener('click', (e) => this.onClick(e));
    }
    onClick(e) {
      // Stop default behaviour from the browser
      e.preventDefault();
      // Add an overflow on the <details> to avoid content overflowing
      this.details.style.overflow = 'hidden';
      // Check if the element is being closed or is already closed
      if (this.isClosing || !this.details.open) {
        this.open();
        // Check if the element is being openned or is already open
      } else if (this.isExpanding || this.details.open) {
        this.shrink();
      }
    }
    shrink() {
      // Set the element as "being closed"
      this.isClosing = true;

      // Store the current height of the element
      const startHeight = `${this.details.offsetHeight}px`;
      // Calculate the height of the summary
      const endHeight = `${this.summary.offsetHeight}px`;

      // If there is already an animation running
      if (this.animation) {
        // Cancel the current animation
        this.animation.cancel();
      }

      // Start a WAAPI animation
      this.animation = this.details.animate({
        // Set the keyframes from the startHeight to endHeight
        height: [startHeight, endHeight]
      }, {
        duration: 250,
        easing: 'ease'
      });

      // When the animation is complete, call onAnimationFinish()
      this.animation.onfinish = () => this.onAnimationFinish(false);
      // If the animation is cancelled, isClosing variable is set to false
      this.animation.oncancel = () => this.isClosing = false;
    }

    open() {
      // Apply a fixed height on the element
      this.details.style.height = `${this.details.offsetHeight}px`;
      // Force the [open] attribute on the details element
      this.details.open = true;
      // Wait for the next frame to call the expand function
      window.requestAnimationFrame(() => this.expand());
    }

    expand() {
      // Set the element as "being expanding"
      this.isExpanding = true;
      // Get the current fixed height of the element
      const startHeight = `${this.details.offsetHeight}px`;
      // Calculate the open height of the element (summary height + content height)
      const endHeight = `${this.summary.offsetHeight + this.content.offsetHeight}px`;

      // If there is already an animation running
      if (this.animation) {
        // Cancel the current animation
        this.animation.cancel();
      }

      // Start a WAAPI animation
      this.animation = this.details.animate({
        // Set the keyframes from the startHeight to endHeight
        height: [startHeight, endHeight]
      }, {
        duration: 400,
        easing: 'ease-out'
      });
      // When the animation is complete, call onAnimationFinish()
      this.animation.onfinish = () => this.onAnimationFinish(true);
      // If the animation is cancelled, isExpanding variable is set to false
      this.animation.oncancel = () => this.isExpanding = false;
    }

    onAnimationFinish(open) {
      // Set the open attribute based on the parameter
      this.details.open = open;
      // Clear the stored animation
      this.animation = null;
      // Reset isClosing & isExpanding
      this.isClosing = false;
      this.isExpanding = false;
      // Remove the overflow hidden and the fixed height
      this.details.style.height = this.details.style.overflow = '';
    }
  }
  customElements.define('collapsible-row', CollapsibleRow);
}

/**
 *  @function addIdToRecentlyViewed
 */
function addIdToRecentlyViewed(handle) {

  if (!handle) {
    let product = document.querySelector('.thb-product-detail');

    if (product) {
      handle = product.dataset.handle;
    }
  }
  if (!handle) {
    return;
  }
  if (window.localStorage) {
    let recentIds = window.localStorage.getItem('recently-viewed');
    if (recentIds != 'undefined' && recentIds != null) {
      window.recentlyViewedIds = JSON.parse(recentIds);
    }
  }
  // Remove current product if already in recently viewed array
  var i = window.recentlyViewedIds.indexOf(handle);

  if (i > -1) {
    window.recentlyViewedIds.splice(i, 1);
  }

  // Add id to array
  window.recentlyViewedIds.unshift(handle);

  if (window.localStorage) {
    window.localStorage.setItem('recently-viewed', JSON.stringify(window.recentlyViewedIds));
  }
}

